/*
 * ============================================================
 * CATEGORIES — assets/ccs-categories.js
 * ============================================================
 * The seller's own groupings of their designs, on the dashboard.
 *
 * ── What this owns, and what the dashboard owns ────────────────────────────
 * The dashboard owns the designs and the grid. This owns the strip above it,
 * the chips on each card, and the question the grid asks on every render:
 * "which designs, in what order?". It answers with `visibleUids`.
 *
 * Nothing here recomputes what the server already decided. Every write comes
 * back with the whole category list and, where it can move, the All items
 * order -- so the strip is redrawn from the answer rather than from a guess
 * about what the answer will be. Counts, order and membership are exactly
 * three things that a single change can all move at once.
 *
 * ── "All items" is not a category ──────────────────────────────────────────
 * It always exists, always comes first, always holds everything including
 * designs in no category at all, and cannot be renamed, reordered or removed.
 * It is represented by a null uid throughout; `data-uid=""` on its pill is
 * how the DOM says the same thing.
 */
(function (window, document) {
  'use strict';

  var api = null;
  var onChange = function () {};

  var state = {
    categories: [],
    allItemsOrder: [],
    defaultUid: null,
    // null is All items. Not a string, so there is no "all" magic value that
    // could collide with a real uid.
    activeUid: null
  };

  function $(selector, scope) { return (scope || document).querySelector(selector); }
  function hook(name, scope) { return (scope || document).querySelector('[data-ccs="' + name + '"]'); }

  function category(uid) {
    return state.categories.filter(function (c) { return c.uid === uid; })[0] || null;
  }

  function activeCategory() { return state.activeUid ? category(state.activeUid) : null; }

  // ── The strip ─────────────────────────────────────────────────────────────

  function renderStrip() {
    var host = hook('cat-strip');
    var template = document.getElementById('ccs-cat-pill');
    var root = $('#ccs-cats');
    if (!host || !template || !root) return;

    // Always shown here, even with no categories: the seller needs somewhere
    // to make their first one, and "All items" beside "+ New category" reads
    // as an invitation. The STORE PAGE does the opposite and hides the strip
    // until there is something in it -- a shopper offered one tab is looking
    // at something broken, not at a feature going unused.
    root.hidden = false;
    host.textContent = '';

    var tabs = [{ uid: null, name: 'All items', count: state.allItemsOrder.length }]
      .concat(state.categories.map(function (c) {
        return { uid: c.uid, name: c.name, count: (c.listingUids || []).length };
      }));

    tabs.forEach(function (tab) {
      var node = template.content.firstElementChild.cloneNode(true);
      node.dataset.uid = tab.uid || '';
      node.classList.toggle('is-active', tab.uid === state.activeUid);
      node.classList.toggle('is-all', !tab.uid);
      hook('cat-name', node).textContent = tab.name;
      hook('cat-count', node).textContent = tab.count;
      hook('cat-default', node).hidden = !tab.uid || tab.uid !== state.defaultUid;
      hook('cat-pick', node).setAttribute('aria-selected', tab.uid === state.activeUid ? 'true' : 'false');
      // All items has nothing to rename, reorder or remove, so it carries no
      // menu -- rather than a menu whose every item is disabled.
      hook('cat-menu', node).hidden = !tab.uid;
      if (tab.uid) node.draggable = true;
      host.appendChild(node);
    });
  }

  // ── Which designs the grid shows ──────────────────────────────────────────

  /*
   * The designs on the open tab, in the order a shopper would see them.
   *
   * `all` is every design the seller has. On All items the server's order is
   * used, so the dashboard and the store page cannot disagree; anything the
   * server did not mention is appended rather than dropped, because a design
   * missing from this list is a design the seller cannot find to fix.
   */
  function visibleUids(all) {
    var have = {};
    all.forEach(function (uid) { have[uid] = true; });

    var source = state.activeUid
      ? ((activeCategory() || {}).listingUids || [])
      : state.allItemsOrder;

    var seen = {};
    var out = [];
    source.forEach(function (uid) {
      if (!have[uid] || seen[uid]) return;
      seen[uid] = true;
      out.push(uid);
    });

    if (!state.activeUid) {
      all.forEach(function (uid) {
        if (seen[uid]) return;
        seen[uid] = true;
        out.push(uid);
      });
    }
    return out;
  }

  // ── The chips on a card ───────────────────────────────────────────────────

  /*
   * Which categories this design is in, and the controls for changing that.
   *
   * The move buttons appear only while a real category is open, because
   * position is a property of a design WITHIN a category -- on All items
   * there is no position to move, only an order derived from the categories.
   */
  function decorateCard(card, listingUid) {
    var host = hook('cats', card);
    if (!host) return;
    host.textContent = '';
    host.dataset.uid = listingUid;

    // On All items this is the store's own order rather than a category's,
    // but it is a position either way, so the controls are the same.
    var open = activeCategory();
    var order = open ? (open.listingUids || []) : (state.allItemsOrder || []);
    var at = order.indexOf(listingUid);
    var last = order.length - 1;
    host.appendChild(moveButton('cat-move-earlier', '←', 'Move earlier', at <= 0));
    host.appendChild(moveButton('cat-move-later', '→', 'Move later', at < 0 || at >= last));

    var mine = state.categories.filter(function (c) {
      return (c.listingUids || []).indexOf(listingUid) > -1;
    });
    mine.forEach(function (c) {
      var chip = document.createElement('span');
      chip.className = 'ccs-chip';
      chip.textContent = c.name;
      host.appendChild(chip);
    });

    var add = document.createElement('button');
    add.type = 'button';
    add.className = 'ccs-chip ccs-chip-add';
    add.dataset.ccs = 'cat-assign';
    add.dataset.uid = listingUid;
    add.textContent = mine.length ? '+' : '+ Category';
    add.setAttribute('aria-label', 'Choose categories for this design');
    host.appendChild(add);
  }

  function moveButton(action, glyph, label, disabled) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'ccs-chip ccs-chip-move';
    button.dataset.ccs = action;
    button.textContent = glyph;
    button.title = label;
    button.setAttribute('aria-label', label);
    button.disabled = !!disabled;
    return button;
  }

  // ── Menus ─────────────────────────────────────────────────────────────────
  //
  // One menu element, moved to whatever opened it. Two menus on screen is two
  // chances to act on the wrong category.

  var menuFor = null;      // category uid the ⋮ menu is open for
  var assignFor = null;    // listing uid the chips popover is open for

  function place(menu, anchor) {
    var box = anchor.getBoundingClientRect();
    menu.hidden = false;
    menu.style.top = (box.bottom + window.scrollY + 6) + 'px';
    // Kept on screen: a menu opened from the last pill in a scrolled strip
    // would otherwise hang off the right edge where it cannot be read.
    var width = menu.offsetWidth || 220;
    var left = Math.min(box.left + window.scrollX, window.scrollX + window.innerWidth - width - 12);
    menu.style.left = Math.max(window.scrollX + 8, left) + 'px';
  }

  function closeMenus() {
    var menu = $('#ccs-cat-menu');
    var assign = $('#ccs-assign');
    if (menu) menu.hidden = true;
    if (assign) assign.hidden = true;
    menuFor = null;
    assignFor = null;
  }

  function openCategoryMenu(uid, anchor) {
    var menu = $('#ccs-cat-menu');
    if (!menu) return;
    menuFor = uid;
    var isDefault = uid === state.defaultUid;
    hook('cat-default-set', menu).hidden = isDefault;
    hook('cat-default-clear', menu).hidden = !isDefault;

    var at = state.categories.map(function (c) { return c.uid; }).indexOf(uid);
    hook('cat-left', menu).disabled = at <= 0;
    hook('cat-right', menu).disabled = at < 0 || at >= state.categories.length - 1;

    place(menu, anchor);
  }

  function openAssign(listingUid, anchor) {
    var popover = $('#ccs-assign');
    var list = hook('assign-list');
    if (!popover || !list) return;
    assignFor = listingUid;
    showError('');
    hook('assign-name').value = '';

    list.textContent = '';
    if (!state.categories.length) {
      var empty = document.createElement('p');
      empty.className = 'ccs-assign-empty';
      empty.textContent = 'No categories yet. Make one below.';
      list.appendChild(empty);
    }

    state.categories.forEach(function (c) {
      var row = document.createElement('label');
      row.className = 'ccs-assign-row';
      var box = document.createElement('input');
      box.type = 'checkbox';
      box.dataset.ccs = 'assign-box';
      box.value = c.uid;
      box.checked = (c.listingUids || []).indexOf(listingUid) > -1;
      var name = document.createElement('span');
      name.textContent = c.name;
      row.appendChild(box);
      row.appendChild(name);
      list.appendChild(row);
    });

    place(popover, anchor);
  }

  function showError(text) {
    var element = hook('assign-error');
    if (!element) return;
    element.textContent = text || '';
    element.hidden = !text;
  }

  // ── Writes ────────────────────────────────────────────────────────────────
  //
  // Every one of these adopts the server's answer rather than patching state
  // locally. A rename changes a name; a membership change moves counts, the
  // All items order and possibly which tab is even still there.

  function adopt(data) {
    if (data.categories) state.categories = data.categories;
    if (data.allItemsOrder) state.allItemsOrder = data.allItemsOrder;
    if (data.store) state.defaultUid = data.store.defaultCategoryUid || null;
    // The open tab may have just been removed.
    if (state.activeUid && !category(state.activeUid)) state.activeUid = null;
    renderStrip();
    onChange();
  }

  function fail(error) {
    window.alert((error && error.message) || 'That could not be saved. Please try again.');
  }

  function create(name, thenAssignTo) {
    return api.createCategory(name).then(function (data) {
      adopt(data);
      if (data.restored) {
        window.alert('"' + data.category.name + '" is back, with the designs that were in it.');
      }
      if (thenAssignTo) {
        var uids = state.categories
          .filter(function (c) { return (c.listingUids || []).indexOf(thenAssignTo) > -1; })
          .map(function (c) { return c.uid; });
        if (uids.indexOf(data.category.uid) === -1) uids.push(data.category.uid);
        return api.setListingCategories(thenAssignTo, uids).then(adopt);
      }
      return null;
    });
  }

  function moveCategory(uid, delta) {
    var uids = state.categories.map(function (c) { return c.uid; });
    var at = uids.indexOf(uid);
    var to = at + delta;
    if (at < 0 || to < 0 || to >= uids.length) return;
    uids.splice(to, 0, uids.splice(at, 1)[0]);
    api.reorderCategories(uids).then(adopt).catch(fail);
  }

/*
 * Move one design one place, in whichever order is on screen.
 *
 * Inside a category that is the category's order. On All items it is the
 * store's own order, which is what a shopper sees on the shop front and in
 * the collection -- so the seller arranges their storefront from the same
 * grid either way.
 */
  function moveListing(listingUid, delta) {
    var open = activeCategory();
    var uids = open ? (open.listingUids || []).slice() : (state.allItemsOrder || []).slice();
    var at = uids.indexOf(listingUid);
    var to = at + delta;
    if (at < 0 || to < 0 || to >= uids.length) return;
    uids.splice(to, 0, uids.splice(at, 1)[0]);
    commitOrder(open, uids);
  }

/*
 * Where an arrangement goes. One place, so a drag and a move button cannot
 * disagree about what they just did.
 */
  function commitOrder(open, uids) {
    var saving = open
      ? api.setCategoryMembers(open.uid, uids)
      : api.reorderListings(uids);
    return saving.then(adopt).catch(fail);
  }

  /*
   * Rename in place, on the pill.
   *
   * The name is edited where it is read, so there is no dialog to lose track
   * of and nothing to reconcile if the seller changes their mind -- Escape
   * just puts the pill back.
   */
  function startRename(uid) {
    var pill = document.querySelector('.ccs-cat[data-uid="' + uid + '"]');
    var label = pill && hook('cat-name', pill);
    if (!label) return;

    var input = document.createElement('input');
    input.type = 'text';
    input.className = 'ccs-cat-rename';
    input.maxLength = 80;
    input.value = (category(uid) || {}).name || '';
    label.replaceWith(input);
    input.focus();
    input.select();

    var done = false;
    function finish(save) {
      if (done) return;
      done = true;
      var name = input.value.trim();
      if (!save || !name || name === (category(uid) || {}).name) { renderStrip(); return; }
      api.renameCategory(uid, name).then(adopt).catch(function (error) {
        fail(error);
        renderStrip();
      });
    }

    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') { event.preventDefault(); finish(true); }
      if (event.key === 'Escape') { event.preventDefault(); finish(false); }
    });
    input.addEventListener('blur', function () { finish(true); });
  }

  function remove(uid) {
    var target = category(uid);
    if (!target) return;
    var count = (target.listingUids || []).length;
    // Says what happens to the designs, because that is the only frightening
    // part and the answer is reassuring.
    var message = 'Remove the "' + target.name + '" category?\n\n' +
      (count
        ? 'The ' + count + ' design' + (count === 1 ? '' : 's') + ' in it stay in your store.'
        : 'It has no designs in it.');
    if (!window.confirm(message)) return;
    api.deleteCategory(uid).then(adopt).catch(fail);
  }

  // ── Dragging ──────────────────────────────────────────────────────────────
  //
  // The enhancement, never the only path: every reorder drag does is also in
  // a ⋮ menu or a move button, because drag is unusable on a phone and with a
  // keyboard.

  function makeSortable(container, itemSelector, commit) {
    var dragging = null;

    container.addEventListener('dragstart', function (event) {
      var item = event.target.closest(itemSelector);
      if (!item || !item.draggable) return;
      dragging = item;
      item.classList.add('is-dragging');
      // Firefox will not start a drag without data on the transfer.
      try { event.dataTransfer.setData('text/plain', item.dataset.uid || ' '); } catch (e) {}
      event.dataTransfer.effectAllowed = 'move';
    });

    container.addEventListener('dragover', function (event) {
      if (!dragging) return;
      event.preventDefault();
      var over = event.target.closest(itemSelector);
      if (!over || over === dragging || !over.draggable) return;
      var box = over.getBoundingClientRect();
      // Past the midpoint means after it. Compared on the axis the items
      // actually flow along, so this works for the strip and for the grid.
      var horizontal = box.width > box.height;
      var past = horizontal
        ? event.clientX > box.left + box.width / 2
        : event.clientY > box.top + box.height / 2;
      over.parentNode.insertBefore(dragging, past ? over.nextSibling : over);
    });

    container.addEventListener('drop', function (event) { event.preventDefault(); });

    container.addEventListener('dragend', function () {
      if (!dragging) return;
      dragging.classList.remove('is-dragging');
      dragging = null;
      var order = Array.prototype.slice
        .call(container.querySelectorAll(itemSelector))
        .filter(function (item) { return item.draggable && item.dataset.uid; })
        .map(function (item) { return item.dataset.uid; });
      commit(order);
    });
  }

  // ── Wiring ────────────────────────────────────────────────────────────────

  // The data-ccs values that DO something, as opposed to the ones naming a
  // slot to write into.
  var ACTIONS = [
    'cat-pick', 'cat-menu', 'cat-rename', 'cat-left', 'cat-right',
    'cat-default-set', 'cat-default-clear', 'cat-remove', 'cat-new',
    'cat-assign', 'cat-move-earlier', 'cat-move-later'
  ];
  var ACTION_SELECTOR = ACTIONS.map(function (name) {
    return '[data-ccs="' + name + '"]';
  }).join(',');

  function wire(grid) {
    var strip = hook('cat-strip');
    if (strip) {
      makeSortable(strip, '.ccs-cat', function (uids) {
        api.reorderCategories(uids).then(adopt).catch(fail);
      });
    }
    if (grid) {
      makeSortable(grid, '.ccs-listing', function (uids) {
        commitOrder(activeCategory(), uids);
      });
    }

    document.addEventListener('click', function (event) {
      // Actions only, not every hook. A click lands on whatever is under the
      // pointer, and a tab is a button carrying the action with the name and
      // the count inside it carrying slots -- so closest('[data-ccs]') found
      // data-ccs="cat-name" and the tab did not switch. Only its padding
      // worked. Listing the actions fixes the class, not the one control.
      var target = event.target.closest(ACTION_SELECTOR);

      // A click anywhere else closes whatever is open, which is the only
      // dismissal people reliably try first.
      if (!target || !/^(cat-menu|cat-assign)$/.test(target.getAttribute('data-ccs'))) {
        if (!event.target.closest('#ccs-cat-menu, #ccs-assign')) closeMenus();
      }
      if (!target) return;

      var pill = target.closest('.ccs-cat');
      var action = target.getAttribute('data-ccs');

      switch (action) {
        case 'cat-pick':
          state.activeUid = pill.dataset.uid || null;
          renderStrip();
          onChange();
          break;

        case 'cat-menu':
          if (menuFor === pill.dataset.uid) { closeMenus(); break; }
          closeMenus();
          openCategoryMenu(pill.dataset.uid, target);
          break;

        case 'cat-rename': {
          var renaming = menuFor;
          closeMenus();
          startRename(renaming);
          break;
        }
        case 'cat-left':  moveCategory(menuFor, -1); closeMenus(); break;
        case 'cat-right': moveCategory(menuFor, 1);  closeMenus(); break;

        case 'cat-default-set':
          api.setDefaultCategory(menuFor).then(adopt).catch(fail);
          closeMenus();
          break;
        case 'cat-default-clear':
          api.setDefaultCategory('').then(adopt).catch(fail);
          closeMenus();
          break;

        case 'cat-remove': {
          var removing = menuFor;
          closeMenus();
          remove(removing);
          break;
        }

        case 'cat-new': startNew(target); break;

        case 'cat-assign':
          if (assignFor === target.dataset.uid) { closeMenus(); break; }
          closeMenus();
          openAssign(target.dataset.uid, target);
          break;

        case 'cat-move-earlier': moveListing(target.closest('[data-ccs="cats"]').dataset.uid, -1); break;
        case 'cat-move-later':   moveListing(target.closest('[data-ccs="cats"]').dataset.uid, 1); break;

        default: break;
      }
    });

    // Ticking a box writes immediately. A popover with a Save button is a
    // popover people close without pressing it.
    document.addEventListener('change', function (event) {
      var box = event.target;
      if (!box.dataset || box.dataset.ccs !== 'assign-box' || !assignFor) return;
      var listingUid = assignFor;
      var uids = Array.prototype.slice
        .call(document.querySelectorAll('[data-ccs="assign-box"]'))
        .filter(function (input) { return input.checked; })
        .map(function (input) { return input.value; });
      api.setListingCategories(listingUid, uids).then(function (data) {
        adopt(data);
        // Redrawn under the pointer, so the ticks match what was just saved.
        var anchor = document.querySelector('[data-ccs="cat-assign"][data-uid="' + listingUid + '"]');
        if (anchor) { assignFor = listingUid; openAssign(listingUid, anchor); }
      }).catch(function (error) {
        box.checked = !box.checked;
        fail(error);
      });
    });

    var form = hook('assign-new');
    if (form) {
      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var input = hook('assign-name');
        var name = input.value.trim();
        if (!name) return;
        var listingUid = assignFor;
        showError('');
        create(name, listingUid).then(function () {
          var anchor = document.querySelector('[data-ccs="cat-assign"][data-uid="' + listingUid + '"]');
          if (anchor) { assignFor = listingUid; openAssign(listingUid, anchor); }
        }).catch(function (error) {
          showError((error && error.message) || 'That could not be saved.');
        });
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeMenus();
    });
  }

  /*
   * Make a category from the strip's own button, with an input in its place.
   * Same reasoning as renaming: typed where it will appear.
   */
  function startNew(button) {
    var form = document.createElement('form');
    form.className = 'ccs-cat-newform';
    var input = document.createElement('input');
    input.type = 'text';
    input.maxLength = 80;
    input.placeholder = 'Category name';
    input.setAttribute('aria-label', 'New category name');
    form.appendChild(input);
    button.replaceWith(form);
    input.focus();

    var done = false;
    function finish(save) {
      if (done) return;
      done = true;
      var name = input.value.trim();
      form.replaceWith(button);
      if (save && name) create(name).catch(fail);
    }
    form.addEventListener('submit', function (event) { event.preventDefault(); finish(true); });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { event.preventDefault(); finish(false); }
    });
    input.addEventListener('blur', function () { finish(true); });
  }

  window.CCSCategories = {
    /*
     * `onChanged` is the dashboard re-rendering its grid. Called whenever the
     * open tab or the order within it moves, which is the only thing out here
     * that this module cannot draw itself.
     */
    init: function (options) {
      api = options.api;
      onChange = options.onChanged || function () {};
      wire(options.grid);
    },
    setData: function (data) {
      state.categories = data.categories || [];
      state.allItemsOrder = data.allItemsOrder || [];
      state.defaultUid = (data.store && data.store.defaultCategoryUid) || null;
      if (state.activeUid && !category(state.activeUid)) state.activeUid = null;
      renderStrip();
    },
    visibleUids: visibleUids,
    decorateCard: decorateCard,
    /*
     * Always. Every design has a position now: inside a category it is that
     * category's order, and on All items it is the store's own, which is what
     * the shop front and the collection are rendered from.
     */
    isOrdering: function () { return true; }
  };
})(window, document);
